import { DiscountIcon, Edit2Icon, RecurringIcon } from '@lastbrand/web-ui/icons'
import { TextLink } from '@lastbrand/web-ui/text-link'
import { css, cva } from '@lastbrand/web-ui-styled-system/css'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useState, useRef, useEffect, useMemo } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { ProductInfoGroup, ProductInfoItem } from '../productInfoGroup'

import { mapPlansToFrequencyOptions, getPlanDetailsByPlanId } from './purchase-type-selector.helpers'
import { PURCHASE_TYPES } from './purchase-type-selector.types'

import type { PurchaseType, PurchaseTypeSelectorProps } from './purchase-type-selector.types'

const containerCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-16',
  marginBlock: 'space-24',
})

const optionCardRecipe = cva({
  base: {
    display: 'flex',
    flexDirection: 'column',
    padding: 'space-16',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: 'lightgray',
    borderRadius: 'radius-sm',
    cursor: 'pointer',
  },
  variants: {
    selected: {
      true: { borderColor: 'black' },
    },
  },
})

const optionHeaderCss = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'space-8',
})

const priceContainerCss = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'space-4',
  marginLeft: 'auto',
})

const strikethroughPriceCss = css({
  textStyle: 'body-16',
  color: 'mediumgray',
  textDecoration: 'line-through',
})

const currentPriceRecipe = cva({
  base: {
    textStyle: 'body-16',
    color: 'black',
  },
  variants: {
    festival: {
      true: { color: 'red-100' },
    },
  },
})

// Mirrors the web-ui Radio primitive; kept a native input because each option card is its own label.
const radioCss = css({
  width: '20px',
  height: '20px',
  marginY: 'space-2',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  borderWidth: '1px',
  borderColor: 'mediumgray',
  borderRadius: 'radius-round',
  position: 'relative',
  flexShrink: 0,
  bg: 'white',
  _checked: {
    borderColor: 'black',
    _after: {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '10px',
      height: '10px',
      borderRadius: 'radius-round',
      bg: 'black',
    },
  },
})

const optionLabelRecipe = cva({
  base: {
    textStyle: 'body-14',
  },
  variants: {
    selected: {
      true: { textStyle: 'body-14-strong' },
    },
  },
})

const expandedDetailsCss = css({
  marginTop: 'space-12',
})

const benefitIconCss = css({ color: 'mediumgray' })

const savingsIconCss = css({ color: 'green-100' })

const savingsTextPromoCss = css({ color: 'red-100' })

// Lazy-load so the drawer's vaul/Radix tree stays out of the PDP/QATC First Load JS.
const FrequencyDrawerDS = dynamic(
  () =>
    import(/* webpackChunkName: "frequency-drawer-ds" */ '@src/components/blocks/frequency-drawer-ds').then(module => ({
      default: module.FrequencyDrawerDS,
    })),
  { ssr: false },
)

export const PurchaseTypeSelectorDS = ({
  subscriptionId,
  isFromPdp,
  value,
  onChange,
  subscribeLabel,
  oneTimeLabel,
  name = 'purchase_type',
  variantSubscriptionPlans,
  selectedPlanId,
  onPlanIdChange,
  formattedSubscriptionPrice,
  subscriptionFestivalSavingPercentage,
  formattedFestivalOfferPrice,
  formattedSalePrice,
  product,
}: PurchaseTypeSelectorProps) => {
  const { t } = useTranslation('common')

  const frequencyOptions = mapPlansToFrequencyOptions(variantSubscriptionPlans)

  const [isFrequencySelectorOpen, setIsFrequencySelectorOpen] = useState(false)
  const previousPlanIdRef = useRef<number | undefined>(selectedPlanId)

  const selectedPlanDetails = useMemo(
    () => getPlanDetailsByPlanId(variantSubscriptionPlans, selectedPlanId),
    [variantSubscriptionPlans, selectedPlanId],
  )

  const handleSelect = (type: PurchaseType) => {
    onChange(type)
    trackOnPurchaseTypeChange(type)
  }

  const handleFrequencyLinkClick = () => {
    previousPlanIdRef.current = selectedPlanId
    setIsFrequencySelectorOpen(true)
  }

  const handleFrequencyChange = (planId: string) => {
    onPlanIdChange(Number(planId))
  }

  const handleFrequencySave = () => {
    const existingFrequency = getPlanDetailsByPlanId(variantSubscriptionPlans, previousPlanIdRef.current)?.frequency
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      entity_type: 'user_selected',
      context: {
        existing_frequency: `${existingFrequency?.interval} ${existingFrequency?.unit}`,
        updated_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
      },
    })
    previousPlanIdRef.current = selectedPlanId
    handleFrequencyClose()
  }

  const handleFrequencyClose = () => {
    onPlanIdChange(previousPlanIdRef.current)
    setIsFrequencySelectorOpen(false)
  }

  const trackOnPurchaseTypeChange = (purchaseType: PurchaseType) => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      entity_type: 'user_selected',
      context: {
        subscriptions_button: purchaseType === PURCHASE_TYPES.SUBSCRIBE,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }

  const isSubscribeSelected = value === PURCHASE_TYPES.SUBSCRIBE

  const subscribeDisplayPrice = formattedSubscriptionPrice
  const subscribeStrikethroughPrice = formattedSalePrice

  const oneTimeDisplayPrice = formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice

  const oneTimeStrikethroughPrice = formattedFestivalOfferPrice && formattedSalePrice ? formattedSalePrice : null

  const currentPriceClassName = currentPriceRecipe({ festival: Boolean(formattedFestivalOfferPrice) })

  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        subscriptions_button: isSubscribeSelected,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        existing_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        updated_frequency: null,
        is_modal: !isFromPdp,
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }, [])

  return (
    <div className={containerCss}>
      <label className={optionCardRecipe({ selected: isSubscribeSelected })}>
        <div className={optionHeaderCss}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.SUBSCRIBE}
            checked={isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.SUBSCRIBE)
            }}
            className={radioCss}
          />
          <span className={optionLabelRecipe({ selected: isSubscribeSelected })}>{subscribeLabel}</span>
          <div className={priceContainerCss}>
            <span className={strikethroughPriceCss}>
              <FormattedPrice amount={subscribeStrikethroughPrice} />
            </span>
            <span className={currentPriceClassName}>
              <FormattedPrice amount={subscribeDisplayPrice} />
            </span>
          </div>
        </div>

        <ProductInfoGroup variant="subscription" className={expandedDetailsCss}>
          {subscriptionFestivalSavingPercentage ? (
            <ProductInfoItem size="sm" icon={<DiscountIcon size="16px" className={benefitIconCss} />}>
              <span>
                <span className={savingsTextPromoCss}>
                  {t('save_on_every_recurring_order_promo_text', {
                    subscriptionPromoDiscount: subscriptionFestivalSavingPercentage,
                  })}
                </span>{' '}
                {t('save_on_every_recurring_order_promo_text_continuation', {
                  subscriptionDiscount: selectedPlanDetails?.benefits?.value,
                })}
              </span>
            </ProductInfoItem>
          ) : (
            <ProductInfoItem size="sm" variant="green" icon={<DiscountIcon size="16px" className={savingsIconCss} />}>
              {t('save_on_every_recurring_order', { subscriptionDiscount: selectedPlanDetails?.benefits?.value })}
            </ProductInfoItem>
          )}

          <ProductInfoItem size="sm" icon={<Edit2Icon size="16px" className={benefitIconCss} />}>
            {t('change_or_cancel_anytime')}
          </ProductInfoItem>
          <ProductInfoItem size="sm" icon={<RecurringIcon size="16px" className={benefitIconCss} />}>
            {t('ships_every_label')}
            <TextLink
              size="lg"
              onClick={() => {
                if (value === PURCHASE_TYPES.ONE_TIME) {
                  handleSelect(PURCHASE_TYPES.SUBSCRIBE)
                } else {
                  handleFrequencyLinkClick()
                }
              }}
            >
              {`${selectedPlanDetails?.frequency.displayLabel} ${selectedPlanDetails?.isDefault ? t('freq_common') : ''}`}
            </TextLink>
          </ProductInfoItem>
        </ProductInfoGroup>
      </label>

      <label className={optionCardRecipe({ selected: !isSubscribeSelected })}>
        <div className={optionHeaderCss}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.ONE_TIME}
            checked={!isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.ONE_TIME)
            }}
            className={radioCss}
          />
          <span className={optionLabelRecipe({ selected: !isSubscribeSelected })}>{oneTimeLabel}</span>
          <div className={priceContainerCss}>
            {oneTimeStrikethroughPrice && (
              <span className={strikethroughPriceCss}>
                <FormattedPrice amount={oneTimeStrikethroughPrice} />
              </span>
            )}
            <span className={currentPriceClassName}>
              <FormattedPrice amount={oneTimeDisplayPrice} />
            </span>
          </div>
        </div>
      </label>

      <FrequencyDrawerDS
        isOpen={isFrequencySelectorOpen}
        title={t('choose_frequency')}
        options={frequencyOptions}
        selectedValue={String(selectedPlanId)}
        onChange={handleFrequencyChange}
        onSave={handleFrequencySave}
        onClose={handleFrequencyClose}
        saveButtonText={t('save')}
      />
    </div>
  )
}

export default PurchaseTypeSelectorDS
